// src/components/InvestmentHighlightsCarousel.js - VERSION ULTRA OPTIMISÉE

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Carousel, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { 
    faChartLine, 
    faCheckCircle, 
    faSpinner, 
    faMoneyBillWave, 
    faPiggyBank, 
    faBuilding,
    faHandshake,
    faCoins,
    faArrowTrendUp
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next'; 
import './InvestmentHighlightsCarousel.css';

// Configuration de l'instance Axios
const api = axios.create({
    baseURL: 'https://api.blackroq.pro/api',
    timeout: 10000 // Timeout ajouté
});

const InvestmentHighlightsCarousel = () => {
    const { t } = useTranslation();
    const [investments, setInvestments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const fetchInvestments = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setLoading(false);
                    setInvestments([]);
                    return;
                }
                
                const res = await api.get('/user/investments', {
                    headers: { 'x-auth-token': token }
                });
                
                if (res.data?.investments && Array.isArray(res.data.investments)) {
                    setInvestments(res.data.investments);
                } else {
                    setInvestments([]);
                }
            } catch (err) {
                console.error("Erreur récupération investissements:", err.message);
                setInvestments([]);
            } finally {
                setLoading(false);
            }
        };
        
        fetchInvestments();
        
        // Auto-rotation du carrousel
        const interval = setInterval(() => {
            setActiveIndex(prev => (prev + 1) % 5); // 5 slides maximum
        }, 5000);
        
        return () => clearInterval(interval);
    }, []);

    // 🔥 OPTIMISATION : useMemo pour éviter les recalculs inutiles
    const stats = useMemo(() => {
        if (investments.length === 0) {
            return {
                ongoing: 0,
                successful: 0,
                totalInvested: 0,
                totalProfit: 0,
                totalReferrals: 0,
                uniqueTypes: []
            };
        }
        
        const ongoingInvestments = investments.filter(inv => 
            inv.status === 'active' || inv.status === 'pending'
        );
        
        const successfulInvestments = investments.filter(inv => 
            inv.status === 'completed'
        );
        
        const totalInvested = investments.reduce((sum, inv) => 
            sum + parseFloat(inv.amountInOriginalCurrency || 0), 0
        );
        
        const totalProfit = successfulInvestments.reduce((sum, inv) => 
            sum + (parseFloat(inv.amountInOriginalCurrency || 0) * 2), 0
        );
        
        const totalReferrals = investments.reduce((sum, inv) => 
            sum + (Number(inv.sponsoredCount) || 0), 0
        );
        
        const uniqueInvestmentTypes = [...new Set(
            investments.map(inv => inv.planName).filter(Boolean)
        )];
        
        return {
            ongoing: ongoingInvestments.length,
            successful: successfulInvestments.length,
            totalInvested,
            totalProfit,
            totalReferrals,
            uniqueTypes: uniqueInvestmentTypes
        };
    }, [investments]);

    // 🔥 OPTIMISATION : useCallback pour la fonction getPlanIcon
    const getPlanIcon = useCallback((planName) => {
        if (!planName) return faChartLine;
        const lowerCaseName = planName.toLowerCase();
        
        if (lowerCaseName.includes('crypto')) return faCoins;
        if (lowerCaseName.includes('immo') || lowerCaseName.includes('immobilier')) return faBuilding;
        if (lowerCaseName.includes('epargne') || lowerCaseName.includes('savings')) return faPiggyBank;
        if (lowerCaseName.includes('trading') || lowerCaseName.includes('forex')) return faArrowTrendUp;
        if (lowerCaseName.includes('startup') || lowerCaseName.includes('venture')) return faMoneyBillWave;
        
        return faChartLine;
    }, []);

    const handleSelect = (selectedIndex) => {
        setActiveIndex(selectedIndex);
    };

    if (loading) {
        return (
            <div className="investment-carousel-loading">
                <Spinner animation="border" variant="primary" size="sm" />
                <span className="ms-2">{t('loading_investments')}</span>
            </div>
        );
    }

    if (investments.length === 0) {
        return (
            <div className="investment-carousel-empty">
                <div className="empty-icon">💼</div>
                <h6>{t('no_investments_title')}</h6>
                <p className="small">{t('start_first_investment')}</p>
                <button className="btn btn-sm btn-primary mt-2">
                    {t('explore_investments')}
                </button>
            </div>
        );
    }
    
    const carouselItems = [
        {
            id: 1,
            icon: faSpinner,
            iconClass: 'fa-spin text-primary',
            title: t('ongoing_investments'),
            value: stats.ongoing,
            subtitle: `${stats.ongoing} ${t(stats.ongoing > 1 ? 'investments_active_plural' : 'investment_active_singular')}`,
            badge: 'live'
        },
        {
            id: 2,
            icon: faCheckCircle,
            iconClass: 'text-success',
            title: t('successful_investments'),
            value: stats.successful,
            subtitle: t('completed_investments_count', { count: stats.successful }),
            badge: 'completed'
        },
        {
            id: 3,
            icon: faHandshake,
            iconClass: 'text-info',
            title: t('total_referrals'),
            value: stats.totalReferrals,
            subtitle: t('referral_count_message', { count: stats.totalReferrals }),
            badge: 'referrals'
        },
        {
            id: 4,
            icon: faChartLine,
            iconClass: 'text-warning',
            title: t('financial_overview'),
            value: null,
            subtitle: (
                <>
                    <div className="stat-line">
                        <span className="stat-label">{t('total_invested')}:</span>
                        <span className="stat-value">{stats.totalInvested.toLocaleString()} FCFA</span>
                    </div>
                    <div className="stat-line">
                        <span className="stat-label">{t('total_profit')}:</span>
                        <span className="stat-value text-success">{stats.totalProfit.toLocaleString()} FCFA</span>
                    </div>
                </>
            ),
            badge: 'stats'
        },
        ...stats.uniqueTypes.slice(0, 3).map((planName, index) => ({
            id: `plan-${index + 5}`,
            icon: getPlanIcon(planName),
            iconClass: 'text-secondary',
            title: planName,
            value: t('plan_subscribed'),
            subtitle: t('plan_investment_count', { 
                count: investments.filter(inv => inv.planName === planName).length,
                planName 
            }),
            badge: 'plan'
        }))
    ];

    return (
        <div className="investment-highlights-carousel">
            <div className="carousel-header">
                <h6 className="carousel-title">
                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                    {t('investment_highlights')}
                </h6>
                <div className="carousel-indicators-custom">
                    {carouselItems.map((_, idx) => (
                        <div 
                            key={idx}
                            className={`indicator ${activeIndex === idx ? 'active' : ''}`}
                            onClick={() => setActiveIndex(idx)}
                        />
                    ))}
                </div>
            </div>
            
            <Carousel 
                activeIndex={activeIndex}
                onSelect={handleSelect}
                controls={carouselItems.length > 1}
                indicators={false}
                interval={5000}
                pause={false}
                wrap={true}
                className="investment-carousel"
            >
                {carouselItems.map((item) => (
                    <Carousel.Item key={item.id} className="carousel-item-compact">
                        <div className="carousel-card">
                            {item.badge && (
                                <span className={`carousel-badge badge-${item.badge}`}>
                                    {item.badge}
                                </span>
                            )}
                            
                            <div className="carousel-icon">
                                <FontAwesomeIcon 
                                    icon={item.icon} 
                                    className={item.iconClass}
                                />
                            </div>
                            
                            <div className="carousel-content">
                                <h5 className="carousel-item-title">{item.title}</h5>
                                
                                {item.value !== null ? (
                                    <h2 className="carousel-item-value">
                                        {item.value}
                                    </h2>
                                ) : (
                                    <div className="carousel-stats">
                                        {item.subtitle}
                                    </div>
                                )}
                                
                                <div className="carousel-item-subtitle">
                                    {typeof item.subtitle === 'string' ? item.subtitle : item.subtitle}
                                </div>
                            </div>
                        </div>
                    </Carousel.Item>
                ))}
            </Carousel>
            
            <div className="carousel-footer">
                <small className="text-muted">
                    {t('carousel_footer', { count: investments.length, total: stats.totalInvested.toLocaleString() })}
                </small>
            </div>
        </div>
    );
};

export default InvestmentHighlightsCarousel;